import { useCallback, useEffect, useRef, useState } from 'react'

import { QueryClient, useQueryClient } from '@tanstack/react-query'

import useABTestValue from '@hooks/useABTestValue'
import useGetPageIdentity from '@hooks/useGetPageIdentity'

export const useShowVideoCombination = () =>
  [
    useABTestValue('combinationVideoWidget') === 'test',
    useGetPageIdentity().isHomePage,
  ].every(Boolean)

const getIsVideoCombinationWrapperCreated = (queryClient: QueryClient) =>
  !!queryClient.getQueryData<boolean>(['video-combination-wrapper-created'])

export const useIsVideoCombinationWrapperCreated = () => {
  const queryClient = useQueryClient()
  const queryCache = queryClient.getQueryCache()
  const unsubscribeFnRef = useRef<() => void>(undefined)

  const [
    isVideoCombinationWrapperCreated,
    setIsVideoCombinationWrapperCreated,
  ] = useState<boolean>(() => getIsVideoCombinationWrapperCreated(queryClient))

  const updateIsVideoCombinationWrapperCreated = useCallback(
    (args: any) => {
      if (
        ['invalidate'].includes(args?.action?.type) &&
        args.query.queryKey[0] === 'growthbook-ready'
      ) {
        setIsVideoCombinationWrapperCreated(
          getIsVideoCombinationWrapperCreated(queryClient),
        )
      }
    },
    [queryClient],
  )

  useEffect(() => {
    unsubscribeFnRef.current = queryCache.subscribe(
      updateIsVideoCombinationWrapperCreated,
    )

    setIsVideoCombinationWrapperCreated(
      getIsVideoCombinationWrapperCreated(queryClient),
    )

    return () => {
      if (unsubscribeFnRef.current) {
        unsubscribeFnRef.current()
      }
    }
  }, [queryCache, queryClient, updateIsVideoCombinationWrapperCreated])

  return isVideoCombinationWrapperCreated
}
